import { inject, Injectable, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { CaseModel, InitialFlipType, NextrayModel } from '@ortho-next/nextray-core';
import { catchError, combineLatest, finalize, from, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { Vector2 } from 'three';
import { getViewPlanImages } from '../../../nextray/Core/ViewPlan';
import { AppModel } from '../../../nextray/Models/AppModel';
import { ViewPlanImages } from '../../../nextray/Utils/ViewPlanUtils';
import { DeviceEnum, Image, ImageOrientationEnum, IntegrationRepository, isGuid, LanguageService, LoaderService, Plan } from '../../core';
import { BaseService, CaseService, ImageService } from '../../services';


/**
 * Handles data of view plan page.
 */
@Injectable({ providedIn: 'root' })
export class ViewService extends BaseService {

	private readonly titleSrv = inject(Title);
	private readonly imgSrv = inject(ImageService);
	private readonly caseSrv = inject(CaseService);
	private readonly route = inject(ActivatedRoute);
	private readonly langSrv = inject(LanguageService);
	private readonly loaderSrv = inject(LoaderService);
	private readonly integrationRepo = inject(IntegrationRepository);

	plan = signal<Plan | null>(null);
	model = signal<AppModel | null>(null);
	images = signal<ViewPlanImages | null>(null);

	/**
	* Prepare canvas component.
	*/
	public init(): Observable<boolean> {
		this.loaderSrv.show();
		const caseGuid = this.route.snapshot.queryParamMap.get('caseGuid');
		return this.loadPlan(caseGuid!).pipe(
			switchMap(plan => this.loadModel(plan)),
			finalize(() => this.loaderSrv.hide())
		);
	}

	private loadPlan(planGuid: string): Observable<Plan> {
		if (!isGuid(planGuid)) return throwError(() => new Error('PlanNotValid'));
		return this.caseSrv.getCase(planGuid).pipe(
			catchError((err: Error) => {
				return err.message === 'ItemNotFound' ? throwError(() => new Error('PlanNotFound')) : throwError(() => err)
			}),
			switchMap(plan => {
				return this.isPlanInvalid(plan) ? throwError(() => new Error('PlanNotValid')) : of(plan);
			}),
			tap(plan => {
				this.setTitle(plan);
				this.plan.set(plan);
			})
		);
	}

	private isPlanInvalid(plan: Plan): boolean {
		return plan.device !== DeviceEnum.Jps;
	}

	private setTitle(plan: Plan): void {
		const titleLbl: string = this.langSrv.labels().APP_TITLE_PATIENT_PLAN;
		this.titleSrv.setTitle(titleLbl.replace('{PAT}', plan.patient.number).replace('{PLAN}', plan.number));
	}

	private loadModel(plan: Plan): Observable<boolean> {
		return this.getJPSModel(plan.id, plan.userGuid).pipe(
			tap(model => model && this.model.set(JSON.parse(model.appStateModel!))),
			switchMap(model => model ? this.setMainModel(plan, model) : of(true))
		);
	}

	private readonly observAP = (plan: Plan) => plan.apImageGuid ? this.imgSrv.getImage(plan.apImageGuid) : of(null);
	private readonly observLT = (plan: Plan) => plan.ltImageGuid ? this.imgSrv.getImage(plan.ltImageGuid) : of(null);

	private setMainModel(plan: Plan, rayModel: NextrayModel): Observable<boolean> {
		return combineLatest([
			this.observAP(plan),
			this.observLT(plan)
		]).pipe(
			map(([imgAP, imgLT]) => this.caseModelMapper(plan, imgAP, imgLT, rayModel)),
			switchMap(caseModel => from(getViewPlanImages(caseModel, new Vector2(1024, 1024)))),
			tap(images => this.images.set(images)),
			map(() => true)
		);
	}

	private caseModelMapper(plan: Plan, imgAP: Image | null, imgLT: Image | null, rayModel: NextrayModel): CaseModel {
		return {
			// plan data
			boneType: plan.boneType,
			version: plan.version,
			type: plan.type,
			referenceType: plan.referenceType,
			side: plan.side,
			isPostOperative: plan.isPostOperative,
			// image AP data
			imgAP: imgAP?.url,
			scaleFactorAP: imgAP?.scaleFactor,
			flipTypeAP: this.flipTypeMapper(imgAP?.orientation) ?? undefined,
			brightnessAP: 0,
			contrastAP: 0,
			// image LT data
			imgLT: imgLT?.url,
			scaleFactorLT: imgLT?.scaleFactor,
			flipTypeLT: this.flipTypeMapper(imgLT?.orientation) ?? undefined,
			brightnessLT: 0,
			contrastLT: 0,
			// ray model
			model: rayModel
		}
	}

	private flipTypeMapper(orientationType?: ImageOrientationEnum): InitialFlipType | null {
		switch (orientationType) {
			case ImageOrientationEnum.Nothing: return null;
			case ImageOrientationEnum.Horizontal: return InitialFlipType.horizontal;
			case ImageOrientationEnum.Vertical: return InitialFlipType.vertical;
			case ImageOrientationEnum.Rotate180: return InitialFlipType.rotate180;
			default: return null;
		}
	}

	private getJPSModel(caseId: string, userId: string): Observable<NextrayModel | undefined> {
		return this.integrationRepo.getJPSModel(caseId, userId).pipe(
			map(response => this.handleApiResponse(response))
		);
	}

}
